import { createTheme } from '@mui/material/styles';
import { createLightPalette, createDarkPalette, COLORS } from './colors';
import { 
  createTextFieldStyles, 
  createButtonStyles, 
  createAlertStyles, 
  createAccordionStyles,
  createCommonComponents 
} from './componentStyles';

// TypeScript module declarations
declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    secondary: string;
    notification: {
      unread: string;
      read: string;
      hover: string;
    };
  }
}

declare module '@mui/material/styles' {
  interface Theme {
    name: string;
  }
  interface ThemeOptions {
    name?: string;
  }
}

declare module '@mui/material/styles/createPalette' {
  interface Palette {
    status: {
      awaria: string;
      naprawione: string;
    };
    borders: {
      primary: string;
      secondary: string;
      accent: string;
    };
  }

  interface PaletteOptions {
    status?: {
      awaria?: string;
      naprawione?: string;
    };
    borders?: {
      primary?: string;
      secondary?: string;
      accent?: string;
    };
  }
}

// Create specialized components that need different handling for dark/light
const createSpecializedComponents = (isDark: boolean) => {
  const textFieldStyles = createTextFieldStyles(isDark);
  const buttonStyles = createButtonStyles(isDark);
  const alertStyles = createAlertStyles(isDark);
  const accordionStyles = createAccordionStyles(isDark);
  const commonComponents = createCommonComponents(isDark);

  return {
    ...commonComponents,
    ...accordionStyles,
    MuiTextField: {
      styleOverrides: textFieldStyles
    },
    MuiFormControl: {
      styleOverrides: textFieldStyles // Same styles as TextField
    },
    MuiButton: {
      styleOverrides: buttonStyles
    },
    MuiAlert: {
      styleOverrides: alertStyles
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: isDark ? COLORS.dark.text.primary : COLORS.light.text.primary
        }
      }
    },
    // Dark theme specific components
    ...(isDark && {
      MuiIconButton: {
        styleOverrides: {
          root: {
            '& .MuiSvgIcon-root': {
              color: COLORS.dark.primary,
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          h6: {
            color: COLORS.dark.text.primary
          },
          h4: {
            color: COLORS.dark.text.primary
          },
          body1: {
            color: '#FFFFFF'
          }
        }
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            '& .MuiTab-root': {
              color: COLORS.dark.text.secondary,
              '&.Mui-selected': {
                color: COLORS.dark.primary
              },
              '& .MuiSvgIcon-root': {
                color: COLORS.dark.text.secondary,
                '&.Mui-selected': {
                  color: COLORS.dark.primary
                }
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: COLORS.dark.primary
            }
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              color: COLORS.dark.primary,
              '& .MuiSvgIcon-root': {
                color: COLORS.dark.primary
              }
            },
            '&:hover': {
              color: COLORS.dark.primary,
              opacity: 0.8,
              '& .MuiSvgIcon-root': {
                color: COLORS.dark.primary,
                opacity: 0.8
              }
            }
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          colorPrimary: {
            backgroundColor: "#333333"
          }
        }
      },
      MuiFab: {
        styleOverrides: {
          root: {
            backgroundColor: COLORS.dark.primary,
          }
        }
      },
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            '&.Mui-disabled': {
              color: '#666666'
            }
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            '&.MuiChip-colorPrimary': {
              color: '#FFFFFF',
              backgroundColor: 'rgba(100, 175, 227, 0.25)',
              borderColor: COLORS.dark.primary,
              '&:hover': {
                backgroundColor: 'rgba(100, 175, 227, 0.35)',
              },
            },
            '&.MuiChip-colorDefault': {
              color: COLORS.dark.text.primary,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            },
          },
          label: {
            fontWeight: 500,
          },
          deleteIcon: {
            color: COLORS.dark.text.primary,
            '&:hover': {
              color: '#FFFFFF',
            },
          },
        },
      },
      MuiDialogContentText: {
        styleOverrides: {
          root: {
            color: `${COLORS.dark.text.primary} !important`,
            fontSize: '0.95rem',
            lineHeight: 1.5,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            color: '#FFFFFF',
            fontWeight: 600,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&.Mui-disabled': {
              '& .MuiOutlinedInput-input': {
                color: `${COLORS.dark.text.disabled} !important`,
                '-webkit-text-fill-color': `${COLORS.dark.text.disabled} !important`,
              },
              '& textarea': {
                color: `${COLORS.dark.text.disabled} !important`,
                '-webkit-text-fill-color': `${COLORS.dark.text.disabled} !important`,
              },
            }
          }
        }
      },
    }),
    // Common toggle buttons
    MuiToggleButton: {
      styleOverrides: {
        root: {
          border: `1px solid ${isDark ? COLORS.dark.borders.primary : 'rgba(0, 0, 0, 0.12)'}`,
          backgroundColor: isDark ? '#1E1E1E' : '#ffffff',
          color: isDark ? '#fff' : 'inherit',
          '&.Mui-selected': {
            backgroundColor: isDark ? '#90caf9' : '#1976d2',
            color: isDark ? '#000' : '#fff',
            '&:hover': {
              backgroundColor: isDark ? '#82b1ff' : '#1565c0',
            },
          },
          '&:hover': {
            backgroundColor: isDark ? 'rgba(144, 202, 249, 0.08)' : 'rgba(25, 118, 210, 0.04)',
          },
          padding: '6px 16px',
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.12)',
          '& .MuiToggleButtonGroup-grouped': {
            margin: 0.5,
            border: 0,
            '&:not(:first-of-type)': {
              borderRadius: 4,
            },
            '&:first-of-type': {
              borderRadius: 4,
            },
          },
        },
      },
    },
  };
};

// Export optimized themes
export const lightTheme = createTheme({
  name: 'light',
  palette: createLightPalette(),
  components: createSpecializedComponents(false)
});

export const darkTheme = createTheme({
  name: 'dark',
  palette: createDarkPalette(),
  components: createSpecializedComponents(true)
}); 